// resources/js/src/components/community/stories/components/StoryModal.tsx

import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import { useAuthStore } from "../../../../../auth/auth.store";
import { useStoryStore } from "../../../../../store/storyStore";
import { timeAgo } from "../../comments/commentHelpers";
import DeleteConfirmDialog from "../../comments/DeleteConfirmDialog";
import { StoryGroup } from "../interfaces/storyTypes";
import { ReactionType } from "../../post/interfaces/types";

/* =========================================================
   Types
   ========================================================= */

interface FloatingReaction {
  id: number;
  emoji: string;
  x: number;
}

interface Props {
  isOpen: boolean;
  groups: StoryGroup[];
  groupIndex: number;
  onNavigateGroup: (index: number) => void;
  onClose: () => void;
}

/* =========================================================
   Constants
   ========================================================= */

const SLIDE_DURATION_MS = 5000;

const QUICK_REACTIONS: Exclude<ReactionType, null>[] = [
  "like",
  "love",
  "haha",
  "wow",
  "sad",
  "angry",
];

/* =========================================================
   Component
   ========================================================= */

const StoryModal: React.FC<Props> = ({
  isOpen,
  groups,
  groupIndex,
  onNavigateGroup,
  onClose,
}) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser);

  const markSeen = useStoryStore((s) => s.markSeen);
  const reactToStory = useStoryStore((s) => s.reactToStory);
  const replyToStory = useStoryStore((s) => s.replyToStory);
  const deleteStory = useStoryStore((s) => s.deleteStory);

  /* ---------- local state ---------- */

  const [slideIndex, setSlideIndex] = useState(0);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [floatingReactions, setFloatingReactions] = useState<FloatingReaction[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  /* ---------- derived ---------- */

  const group = groups[groupIndex];
  const story = group?.stories[slideIndex];
  const slideCount = group?.stories.length ?? 0;

  /* ---------- refs ---------- */

  const startedAtRef = useRef<number>(0);
  const pausedAtRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  /* =========================================================
     Effects
     ========================================================= */

  // reset كامل عند تغيير المجموعة أو الفتح
  useEffect(() => {
    setSlideIndex(0);
    setReplyText("");
    setIsPaused(false);
    setProgress(0);
    startedAtRef.current = Date.now();
    pausedAtRef.current = null;
  }, [groupIndex, isOpen]);

  // reset التقدم عند تغيير الـ slide
  useEffect(() => {
    startedAtRef.current = Date.now();
    pausedAtRef.current = null;
    setProgress(0);
  }, [slideIndex]);

  // markSeen
  useEffect(() => {
    if (isOpen && story && !story.seen) {
      markSeen(story.id);
    }
  }, [isOpen, story?.id, story?.seen, markSeen]);

  /* =========================================================
     Navigation (with auto-close at end)
     ========================================================= */

  const handleNextGroup = useCallback(() => {
    const next = groupIndex + 1;
    if (next >= groups.length) {
      onClose(); // آخر مجموعة → اقفل
      return;
    }
    onNavigateGroup(next);
  }, [groupIndex, groups.length, onNavigateGroup, onClose]);

  const handlePrevGroup = useCallback(() => {
    const prev = groupIndex - 1;
    if (prev < 0) return; // أول مجموعة → اثبت
    onNavigateGroup(prev);
  }, [groupIndex, onNavigateGroup]);

  const handleNextSlide = useCallback(() => {
    if (slideIndex < slideCount - 1) setSlideIndex((i) => i + 1);
    else handleNextGroup();
  }, [slideIndex, slideCount, handleNextGroup]);

  const handlePrevSlide = useCallback(() => {
    if (slideIndex > 0) setSlideIndex((i) => i - 1);
    else handlePrevGroup();
  }, [slideIndex, handlePrevGroup]);

  /* =========================================================
     Auto-advance timer
     ========================================================= */

  useEffect(() => {
    if (!isOpen || !story || showDeleteDialog) return;
    if (isPaused) return;

    // resume: عوّض الوقت اللي كان متوقف
    if (pausedAtRef.current !== null) {
      startedAtRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }

    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const ratio = Math.min(elapsed / SLIDE_DURATION_MS, 1);
      setProgress(ratio);

      if (elapsed >= SLIDE_DURATION_MS) {
        handleNextSlide();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isOpen, story?.id, isPaused, showDeleteDialog, handleNextSlide]);

  /* =========================================================
     Pause / resume
     ========================================================= */

  useEffect(() => {
    if (isPaused) {
      pausedAtRef.current = Date.now();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    }
  }, [isPaused]);

  const togglePause = () => setIsPaused((v) => !v);

  /* =========================================================
     Keyboard
     ========================================================= */

  useEffect(() => {
    if (!isOpen || showDeleteDialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") handleNextSlide();
      else if (e.key === "ArrowLeft") handlePrevSlide();
      else if (e.key === " ") {
        e.preventDefault();
        togglePause();
      } else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, showDeleteDialog, handleNextSlide, handlePrevSlide, onClose]);

  /* =========================================================
     Actions
     ========================================================= */

  const handleReact = async (r: ReactionType) => {
    if (!isAuthenticated || !story) return;
    await reactToStory(story.id, r);
    if (!r) return;

    const id = Date.now();
    const x = Math.random() * 80 + 10;
    setFloatingReactions((prev) => [
      ...prev,
      { id, emoji: mapReactionToEmoji(r), x },
    ]);
    setTimeout(() => {
      setFloatingReactions((prev) => prev.filter((x) => x.id !== id));
    }, 1000);
  };

  const handleSendReply = async () => {
    if (!isAuthenticated || !story || !replyText.trim() || sendingReply) return;
    setSendingReply(true);
    const ok = await replyToStory(story.id, replyText.trim());
    setSendingReply(false);
    if (ok) setReplyText("");
  };

  const confirmDeleteStory = async () => {
    if (!story) return;
    setDeleting(true);
    const ok = await deleteStory(story.id);
    setDeleting(false);
    setShowDeleteDialog(false);

    if (ok) {
      if (slideCount <= 1) onClose();
      else setSlideIndex(0);
    }
  };

  /* =========================================================
     Early returns
     ========================================================= */

  if (!isOpen || !group || !story) return null;

  const currentSlide = story.media[0];
  if (!currentSlide) return null;

  const avatarFallback = (name: string) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      name
    )}&background=10b981&color=fff&size=64&bold=true`;

  /* =========================================================
     Render
     ========================================================= */

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={onClose}
        >
          <div className="flex items-center justify-center min-h-screen text-center p-4">
            {/* backdrop */}
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            />

            {/* =====================================================
                Panel — حجم ثابت
                ===================================================== */}
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
              className="
                relative
                w-[420px] h-[760px]
                max-w-full max-h-[95vh]
                rounded-2xl
                bg-black
                overflow-hidden
                shadow-2xl
              "
            >
              <div
                className="relative w-full h-full flex flex-col"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                {/* =================================================
                    Media area (top) — حجم ثابت
                    ================================================= */}
                <div className="relative w-full flex-1 min-h-0 bg-black">
                  {/* progress bars */}
                  <div className="absolute top-2 left-2 right-2 z-30 flex gap-1">
                    {group.stories.map((_, i) => {
                      const isPast = i < slideIndex;
                      const isCurrent = i === slideIndex;
                      return (
                        <div
                          key={i}
                          className="h-0.5 flex-1 rounded-full bg-white/30 overflow-hidden"
                        >
                          <div
                            className="h-full bg-white transition-none"
                            style={{
                              width: isPast
                                ? "100%"
                                : isCurrent
                                ? `${progress * 100}%`
                                : "0%",
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* header: avatar + name + time */}
                  <div className="absolute top-5 left-3 z-30 flex items-center gap-2">
                    <img
                      src={
                        group.author.avatarUrl ||
                        avatarFallback(group.author.name)
                      }
                      alt={group.author.name}
                      className="w-8 h-8 rounded-full object-cover border border-white/50"
                    />
                    <span className="text-white text-sm font-semibold drop-shadow">
                      {group.author.name}
                    </span>
                    <span className="text-white/70 text-xs drop-shadow">
                      {timeAgo(story.createdAt)}
                    </span>
                    {isPaused && (
                      <span className="text-white/70 text-xs">⏸</span>
                    )}
                  </div>

                  {/* delete + close */}
                  <div className="absolute top-2 right-2 z-30 flex items-center gap-1">
                    {story.isOwner && (
                      <button
                        aria-label="Delete story"
                        onClick={() => setShowDeleteDialog(true)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-white bg-black/30 hover:bg-black/60 transition"
                      >
                        <Icon
                          icon="mdi:trash-can-outline"
                          className="w-5 h-5"
                        />
                      </button>
                    )}
                    <button
                      aria-label="Close"
                      onClick={onClose}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-white text-xl font-bold bg-black/30 hover:bg-black/60 transition"
                    >
                      ✕
                    </button>
                  </div>

                  {/* media */}
                  {currentSlide.type === "video" ? (
  <video
    key={currentSlide.id}
    src={currentSlide.url}
    autoPlay
    muted
    loop
    playsInline
    className="absolute inset-0 w-full h-full object-contain bg-black"
  />
) : (
  <img
    key={currentSlide.id}
    src={currentSlide.url}
    alt="Story"
    className="absolute inset-0 w-full h-full object-contain bg-black"
  />
)}

                  {/* زر شفاف للإيقاف/التشغيل */}
                  <button
                    type="button"
                    aria-label={isPaused ? "Resume" : "Pause"}
                    onClick={togglePause}
                    className="absolute inset-0 z-20 bg-transparent"
                  />

                  {/* floating reactions */}
                  {floatingReactions.map((r) => (
                    <span
                      key={r.id}
                      style={{ left: `${r.x}%` }}
                      className="absolute bottom-4 text-2xl z-20 animate-float-up"
                    >
                      {r.emoji}
                    </span>
                  ))}

                  {/* nav arrows */}
                  <button
                    aria-label="Previous"
                    className="absolute left-1 top-1/2 -translate-y-1/2 text-white text-3xl w-9 h-9 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/60 z-30"
                    onClick={handlePrevSlide}
                  >
                    ‹
                  </button>
                  <button
                    aria-label="Next"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-white text-3xl w-9 h-9 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/60 z-30"
                    onClick={handleNextSlide}
                  >
                    ›
                  </button>
                </div>

                {/* =================================================
                    Bottom panel — حجم ثابت
                    ================================================= */}
                <div className="shrink-0 bg-black/90 border-t border-white/10 p-3 space-y-2 max-h-[45%] overflow-y-auto">
                  {/* reactions */}
                  {isAuthenticated ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() =>
                          handleReact(
                            story.myReaction === "like" ? null : "like"
                          )
                        }
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                          story.myReaction
                            ? "bg-white/25 text-white"
                            : "bg-white/10 text-white hover:bg-white/20"
                        }`}
                      >
                        <Icon
                          icon={
                            story.myReaction
                              ? getReactionIcon(story.myReaction)
                              : "mdi:thumb-up-outline"
                          }
                          className={`w-4 h-4 ${
                            story.myReaction
                              ? getReactionColor(story.myReaction)
                              : ""
                          }`}
                        />
                        <span>
                          {story.myReaction
                            ? getReactionLabel(story.myReaction)
                            : "Like"}
                        </span>
                      </button>

                      <div className="flex items-center gap-1">
                        {QUICK_REACTIONS.filter((r) => r !== "like").map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() =>
                              handleReact(story.myReaction === r ? null : r)
                            }
                            aria-label={getReactionLabel(r)}
                            title={getReactionLabel(r)}
                            className={`text-xl transition-transform hover:scale-125 ${
                              story.myReaction === r ? "scale-125" : ""
                            }`}
                          >
                            {mapReactionToEmoji(r)}
                          </button>
                        ))}
                      </div>

                      {story.reactionsCount > 0 && (
                        <span className="text-xs text-white/70 ml-auto">
                          {story.reactionsCount}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-white/50">Sign in to react</p>
                  )}

                  {/* reply box */}
                  {isAuthenticated && (
                    <div className="flex items-center gap-2">
                      <img
                        src={
                          currentUser?.avatarUrl ||
                          avatarFallback(currentUser?.name ?? "You")
                        }
                        alt="You"
                        className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                      />
                      <input
                        type="text"
                        className="flex-1 p-2 rounded-full border border-white/20 bg-white/10 text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/40"
                        placeholder={`Reply to ${group.author.name}...`}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply();
                          }
                        }}
                        disabled={sendingReply}
                      />
                      <button
                        className="bg-white text-black p-2 rounded-full disabled:opacity-50 flex items-center justify-center"
                        onClick={handleSendReply}
                        disabled={!replyText.trim() || sendingReply}
                        aria-label="Send reply"
                      >
                        {sendingReply ? (
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                        ) : (
                          <Icon icon="mdi:send" className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}

                  {/* replies list */}
                  {story.replies && story.replies.length > 0 && (
                    <div className="max-h-24 overflow-y-auto text-xs text-white/90 space-y-1 pt-1">
                      {story.replies.map((r) => (
                        <div key={r.id} className="flex gap-2 items-start">
                          <img
                            src={
                              r.user.avatarUrl || avatarFallback(r.user.name)
                            }
                            alt={r.user.name}
                            className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5"
                          />
                          <div className="flex-1">
                            <strong className="text-white">
                              {r.user.name}:
                            </strong>{" "}
                            <span className="text-white/80">{r.text}</span>
                            <span className="text-white/50 text-[10px] ml-1">
                              {timeAgo(r.createdAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>

        {/* floating reaction animation */}
        <style>{`
          @keyframes floatUp {
            0% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-80px); }
          }
          .animate-float-up {
            animation: floatUp 1s ease-out forwards;
            position: absolute;
          }
        `}</style>
      </Transition.Root>

      {/* delete confirmation */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => !deleting && setShowDeleteDialog(false)}
        onConfirm={confirmDeleteStory}
        title="Delete Story"
        message="Are you sure you want to delete this story? This action cannot be undone."
        loading={deleting}
      />
    </>
  );
};

/* =========================================================
   Helpers
   ========================================================= */

function mapReactionToEmoji(r: ReactionType): string {
  switch (r) {
    case "like":
      return "👍";
    case "love":
      return "❤️";
    case "haha":
      return "😂";
    case "wow":
      return "😮";
    case "sad":
      return "😢";
    case "angry":
      return "😠";
    default:
      return "👍";
  }
}

function getReactionIcon(r: ReactionType): string {
  switch (r) {
    case "like":
      return "mdi:thumb-up";
    case "love":
      return "mdi:heart";
    case "haha":
      return "mdi:emoticon-happy";
    case "wow":
      return "mdi:emoticon-surprised";
    case "sad":
      return "mdi:emoticon-cry";
    case "angry":
      return "mdi:emoticon-angry";
    default:
      return "mdi:thumb-up-outline";
  }
}

function getReactionColor(r: ReactionType): string {
  switch (r) {
    case "like":
      return "text-blue-400";
    case "love":
      return "text-red-400";
    case "haha":
      return "text-yellow-400";
    case "wow":
      return "text-yellow-400";
    case "sad":
      return "text-yellow-400";
    case "angry":
      return "text-orange-400";
    default:
      return "";
  }
}

function getReactionLabel(r: ReactionType): string {
  switch (r) {
    case "like":
      return "Like";
    case "love":
      return "Love";
    case "haha":
      return "Haha";
    case "wow":
      return "Wow";
    case "sad":
      return "Sad";
    case "angry":
      return "Angry";
    default:
      return "Like";
  }
}

export default StoryModal;